import React from 'react'
import { Button, Form, Popover, Result, Select, Skeleton, Typography, Modal, Divider, message } from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons'
import { FormComponent } from '../../../BaseComponent'
import { VerticalTableTransfer, TableColumn } from '../../../VerticalTableTransfer'
import ShelterQuestionAskAlias, { QuestionAskAlias } from '../../ShelterQuestionAskAlias'
import { ShelterContextData } from '../../../../context/ShelterContext'
import { SpecieContextData } from '../../../../context/SpecieContext'
import { QuestionContextData } from '../../../../context/QuestionContext'
import { QuestionTemplateContextData } from '../../../../context/QuestionTemplateContext'
import IQuestion from '../../../../interfaces/IQuestion'
import IQuestionShelter from '../../../../interfaces/IQuestionShelter'
import { ShelterQuestionUpdateRequest } from '../../../../services/api/interfaces/ApiRequest'
import { LanguageContextData } from '../../../../context/LanguageContext'

type ShelterQuestionProps = {
  shelterUuid: string

  context: ShelterContextData
  languageContext: LanguageContextData
  speciesContext: SpecieContextData
  questionContext: QuestionContextData
  questionTemplateContext: QuestionTemplateContextData
}
export default class ShelterQuestion extends FormComponent<ShelterQuestionProps> {

  state = {
    loaded: false,
    selectedTemplateId: null as number | null,
    speciesOptions: [] as any[],
    shelterQuestionSpecies: [] as any[],
    askAliasPopover: {} as Record<any, any>,
    questionShelter: [] as IQuestionShelter[],
  }

  private sourceTransferRef: any

  private tableTransferColumns: TableColumn = {
    source: [
      {
        dataIndex: 'uuid',
        title: 'uuid',
        searchable: true
      },
      {
        title: 'Group',
        dataIndex: ['group', 'name'],
        render: title => (
          <Typography.Text strong style={{fontSize: 12}}>{title}</Typography.Text>
        ),
        searchable: true
      },
      {
        dataIndex: 'ask',
        title: 'Question',
        searchable: true
      }
    ],
    target: [
      {
        dataIndex: 'action',
        title: 'Move',
        render: (text, record) => {
          const question = this.getSingleQuestionShelter(record.uuid)
          if (!question) return null

          const lastRecord = this.state.questionShelter[this.state.questionShelter.length - 1]
          const canGoUp = question.position > 0
          const canGoDown = question.question_uuid !== lastRecord.question_uuid

          return (
            <>
              <Button size="small" type="dashed" shape="circle" icon={<ArrowUpOutlined />} disabled={!canGoUp} style={{marginBottom: 5, marginRight: 5}} onClick={() => this.handlePositionChange(question, 'up')} />
              <Button size="small" type="dashed" shape="circle" icon={<ArrowDownOutlined />} disabled={!canGoDown} onClick={() => this.handlePositionChange(question, 'down')} />
            </>
          )
        }
      },
      {
        dataIndex: 'species',
        title: 'Species',
        render: (text: any, record: any) => {
          const question = this.getSingleQuestionShelter(record.uuid)
          if (!question) return null

          const questionSpecies = this.state.shelterQuestionSpecies?.find(p => p.question_uuid === question.question_uuid)

          return (
            <div style={{width: 120}}>
              <Select
                mode="multiple"
                placeholder="Select species"
                options={this.state.speciesOptions}
                defaultValue={questionSpecies?.species || []}
                allowClear={false}
                showSearch={true}
                style={{width: '100%'}}
                onSelect={(specie_id) => this.handleSpecieSelect(question, specie_id)}
                onDeselect={(specie_id) => this.handleSpecieDeselect(question, specie_id)}
              />
            </div>
          )
        }
      },
      {
        title: 'Group',
        dataIndex: ['group', 'name'],
        render: title => (
          <Typography.Text strong style={{fontSize: 12}}>{title}</Typography.Text>
        )
      },
      {
        dataIndex: 'ask',
        title: 'Question',
        searchable: true
      },
      {
        dataIndex: 'ask_alias',
        title: 'Question Alias',
        render: (text: any, record: IQuestion) => {
          const question = this.getSingleQuestionShelter(record.uuid)
          if (!question) return null
          if (!question.question_uuid) return null

          const questionUuid: string = question.question_uuid
          const askAlias: string = question.ask_alias || ''

          return (
            <Popover
              title="Set Question Alias"
              content={(
                <ShelterQuestionAskAlias
                  question={question}
                  questionAskAlias={{
                    ask_alias: askAlias,
                    translation: question.translation?.map(v => {
                      return {
                        ask_alias: v.ask_alias,
                        language_id: v.language_id,
                        language: v.language
                      }
                    }) || []
                  }}
                  onSave={this.handleSetAskAlias}
                />
              )}
              placement="left"
              trigger="click"
              visible={this.state.askAliasPopover[questionUuid]}
              onVisibleChange={(visible: boolean) => this.handleAskAliasPopoverChange(questionUuid, visible)}
            >
              <Typography.Link>
                {question.ask_alias?.length ? 'Change Question alias' : 'Set Question Alias'}
              </Typography.Link>
            </Popover>
          )
        }
      }
    ]
  }

  async componentDidMount() {
    await this.load()

    const questionShelter = this.getAllQuestionShelter()
    const shelterQuestionSpecies = questionShelter.filter(v => v.question_shelter_species?.length).map(v => {
      return {
        question_uuid: v.question_uuid,
        species: v.question_shelter_species?.map(x => x.specie_id)
      }
    })

    this.setState({
      loaded: true,
      speciesOptions: this.props.speciesContext.species?.map(v => {
        return {
          label: v.name,
          value: v.id
        }
      }),
      shelterQuestionSpecies,
      questionShelter
    }, () => {
      const repeated = questionShelter.map(v => v.position).some((pos, index, self) => self.indexOf(pos) !== index)
      
      if (repeated) {
        if (this.sourceTransferRef) {
          setTimeout(() => {
            this.sourceTransferRef.scrollIntoView()
          }, 500)
        }

        Modal.confirm({
          icon: null,
          content: (
            <Result
              status="warning"
              title="Fix Repeated Positions"
              subTitle="We found some repeated questions with same position. We must fix them :D"
            />
          ),
          okText: 'Fix now!',
          cancelText: 'I will fix them later',
          onOk: () => this.handleFixQuestions()
        })
      }
    })
  }

  private async load() {
    const { shelterUuid, context, speciesContext, languageContext, questionContext, questionTemplateContext } = this.props

    const options = {
      _force_ids: true,
      limit:9999
    }

    await speciesContext.requestSpecieList({
      options
    })

    await languageContext.requestLanguageList({
      options
    }) 

    await questionContext.requestQuestionList({
      search: {
        specie: {
          with: true
        },
        group: {
          with: true
        }
      },
      options
    })

    await questionTemplateContext.requestQuestionTemplateList({
      search: {
        questions: {
          with: true
        }
      },
      options
    })

    if (shelterUuid) {
      await context.requestShelterFind({
        search: {
          uuid: shelterUuid,
          contract_options: {
            with: {
              deleted: false
            }
          },
          organization_question_shelters: {
            with: {
              deleted: false,
              kiosk_reason_id: null,
              question_shelter_species: {
                with: true
              },
              question: {
                with: true
              },
              translation: {
                with: true
              }
            }
          },
          organization_branches: {
            with: {
              deleted: false,
              kiosk_reasons: {
                with: {
                  deleted: false
                }
              },
              kiosks: {
                with: {
                  active: true,
                  deleted: false
                }
              }
            }
          }
        },
        options: {
          _force_ids: true
        }
      })
    }
  }

  private getAllQuestionShelter = () => {
    const { context, questionContext: { questions } } = this.props
    if (!context.shelter?.organization_question_shelters) return []

    const shelterQuestions: IQuestionShelter[] = []

    context.shelter.organization_question_shelters.forEach(v => {
      const question = questions?.find(x => x.id === v.question_id)

      if (question) {
        shelterQuestions.push({
          ...v,
          position: v.position || question.position || 0,
          question_uuid: question.uuid
        })
      }
    })

    return shelterQuestions.sort((a: any, b: any) => a.position - b.position)
  }

  private getSingleQuestionShelter = (questionUuid: string) => {
    const { questionShelter } = this.state

    return questionShelter.find(v => v.question_uuid === questionUuid)
  }

  private updateShelterQuestion = (updated: IQuestionShelter) => {
    const { questionShelter } = this.state

    const index = questionShelter.findIndex(v => v.question_uuid === updated.question_uuid)
    if (index >= 0) {
      questionShelter[index] = updated
    } else {
      questionShelter.push(updated)
    }

    this.setState({
      questionShelter
    })
  }

  private handleSetAskAlias = (questionShelter: IQuestionShelter, questionAskAlias: QuestionAskAlias) => {
    questionShelter.ask_alias = questionAskAlias.ask_alias
    questionShelter.translation = questionAskAlias.translation?.map(v => {
      return {
        ask_alias: v.ask_alias,
        language_id: v.language_id,
      }
    }) || [] as any
    
    this.updateShelterQuestion(questionShelter)

    if (questionShelter.question_uuid) {
      this.handleAskAliasPopoverChange(questionShelter.question_uuid, false)
    }
  }

  private handleAskAliasPopoverChange = (questionUuid: string, visible: boolean) => {
    this.setState((state: any) => ({
      ...state,
      askAliasPopover: {
        ...state.askAliasPopover,
        [questionUuid]: visible
      }
    }))
  }

  private handleTableTransferChange = (selectedKey: any[]) => {
    const { questionContext } = this.props
    const { questionShelter } = this.state

    const changed = questionShelter.filter(v => selectedKey.includes(v.question_uuid))
    selectedKey.forEach(uuid => {
      const exists = !!questionShelter.find(v => v.question_uuid === uuid)
      const question = questionContext.questions?.find(v => v.uuid === uuid)

      if (!exists && question) {
        changed.push({
          ask_alias: null,
          question_id: question.id,
          question_uuid: uuid,
          position: changed.length
        } as any)
      }
    })

    this.setState({
      questionShelter: changed
    })
  }

  private handlePositionChange = (record: any, direction: string) => {
    const { questionShelter } = this.state

    let selected = Object.assign([], questionShelter) as IQuestionShelter[]
    for (let question of selected) {
      // Moving up
      if (direction === 'up') {
        // Cannot go up if position is zero or lower :P
        if (record.position > 0) {
          // Increase one position if there's any question in the new record position
          if (question.position === (record.position - 1)) {
            question.position = question.position + 1
          }

          // Update question position
          if (question.question_uuid === record.question_uuid) {
            question.position = record.position - 1
          }
        }
      }

      // Moving down
      if (direction === 'down') {
        // Decrease one position if there's any question in the new record position
        if (question.question_uuid !== record.question_uuid && question.position === record.position) {
          question.position = question.position - 1
        }

        // Update question position
        if (question.question_uuid === record.question_uuid) {
          question.position = record.position + 1
        }
      }
    }

    setTimeout(() => {
      this.setState({
        questionShelter: selected.sort((a: any, b: any) => a.position - b.position)
      })
    }, 100)
  }

  private handleFixQuestions = () => {
    const { questionShelter } = this.state

    this.setState({
      questionShelter: questionShelter.map((v, index) => {
        return {
          ...v,
          position: index
        }
      })
    })
  }

  private handleSpecieSelect = (question: IQuestionShelter, specie_id: number) => {
    const { shelterQuestionSpecies } = this.state

    const index = shelterQuestionSpecies.findIndex(p => p.question_uuid === question.question_uuid)
    if (index >= 0) {
      shelterQuestionSpecies[index].species.push(specie_id)
    } else {
      shelterQuestionSpecies.push({
        question_uuid: question.question_uuid,
        species: [specie_id]
      })
    }

    this.setState({
      shelterQuestionSpecies
    })
  }

  private handleSpecieDeselect = (question: IQuestionShelter, specie_id: number) => {
    const { shelterQuestionSpecies } = this.state

    const index = shelterQuestionSpecies.findIndex(p => p.question_uuid === question.question_uuid)
    if (index >= 0) {
      shelterQuestionSpecies[index].species = shelterQuestionSpecies[index].species.filter(p => p !== specie_id)
    }

    this.setState({
      shelterQuestionSpecies
    })
  }

  private handleSave = async (values: any) => {
    const { shelterUuid, context } = this.props
    const { questionShelter, shelterQuestionSpecies } = this.state

    try {
      const payload: ShelterQuestionUpdateRequest = {
        organization_shelter_uuid: shelterUuid,
        template_id: values.template_id,
        questions: questionShelter.map(v => {
          const questionSpecies = shelterQuestionSpecies.find(p => p.question_uuid === v.question_uuid)
  
          return {
            question_id: v.question_id,
            position: v.position,
            ask_alias: v.ask_alias,
            translation: v.translation,
            species: questionSpecies?.species || []
          }
        })
      }
      
      await context.requestShelterQuestionUpdate(payload)
      message.success('Questions saved successfully!')
    } catch (error) {
      console.log(error)
      message.error(`Failed to save questions. Reason: ${error}`) 
    }    
  }

  render() {
    const { context, questionContext, questionTemplateContext } = this.props
    const { loaded, questionShelter, selectedTemplateId } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    if (!context.shelter) {
      return <Result
        status="404"
        title="Shelter not found"
      />
    }

    const questionTemplatesOptions = questionTemplateContext.questionTemplates?.map(template => {
      return {
        label: template.name,
        value: template.id
      }
    }) as any

    let questionDataSource = questionContext.questions?.map(v => {
      const shelter = questionShelter.find(x => x.question_uuid === v.uuid)

      return {
        ...v,
        position: shelter?.position || v.position || 0
      }
    }) || []

    if (selectedTemplateId) {
      const selectedTemplate = questionTemplateContext.questionTemplates?.find(t => t.id === selectedTemplateId)
      if (selectedTemplate?.questions) {
        const templateQuestionIds = selectedTemplate.questions.map(q => q.question_id)
        const transferredQuestionIds = questionShelter.map(q => q.question_id)
        questionDataSource = questionDataSource.filter(q => templateQuestionIds.includes(q.id) || transferredQuestionIds.includes(q.id))
      }
    }

    const values = {
      template_id: context.shelter?.template_id
    }

    return (
      <Form
        name="organization_questions"
        initialValues={values}
        layout="vertical"
        onFinish={this.handleSave}
      >
        <Form.Item
          label="Question Template"
          name="template_id"
        >
          <Select
            placeholder="Select a template"
            options={questionTemplatesOptions}
            allowClear={true}
            onChange={(value) => {
              this.setState({ selectedTemplateId: value })
            }}
          />
        </Form.Item>

        <Typography.Title level={5}>Configure the questions in the adoption inquiry form</Typography.Title>
        <Typography.Text>The view entitled with "source" represents the full questions library available in the Adopets database. Use the Search button within this view to more easily find the questions you want to add to the shelter/rescue.
The other view entitle with "target" represents the currently configured questions for this shelter/rescue.</Typography.Text>

        <VerticalTableTransfer
          rowKey="uuid"
          columns={this.tableTransferColumns}
          dataSource={questionDataSource.sort((a, b) => a.position - b.position)}
          intialSelectedKeys={questionShelter.map(v => v.question_uuid)}
          onChange={this.handleTableTransferChange}
        />
        <div ref={refs => this.sourceTransferRef = refs} />

        <Divider />

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={!!context.fetching} size="large" ref={(refs) => this.actionRefs.save = refs} block>
            Save
          </Button>
        </Form.Item>

      </Form>
    )
  }
}
